/**
 * The locales digitopia.ai speaks.
 *
 * This site is multilingual from the first commit rather than "English now,
 * translations later", because retrofitting locales means touching every page,
 * every link and every `<head>` — and the whole point of the preview branch is
 * that page work is the expensive part we want to do once.
 *
 * Adding a locale is one entry here plus one file in `src/content/`. Nothing
 * else in the app enumerates languages: routing, the switcher, `hreflang`, the
 * sitemap and the prerender pass all read this list.
 *
 * **Codes are language-only, not language-region** (`pt`, not `pt-BR`). One
 * Portuguese page ranking for both Brazil and Portugal beats two half-written
 * ones, and splitting a locale later is additive — `pt` stays as the fallback
 * for `pt-PT` the day someone writes it. Going the other way, merging two
 * regional variants back into one, means choosing which copy to throw away.
 */
export interface LocaleDef {
  /** URL segment and `<html lang>`. BCP 47. */
  code: string
  /**
   * What `og:locale` and `og:locale:alternate` publish — `language_TERRITORY`,
   * with an underscore, which is the ONE place on this site where a locale is
   * not written as its BCP 47 tag.
   *
   * The Open Graph protocol defines the field that way and names `en_US` as the
   * default; a bare `en` is not a value it specifies, and Facebook's own
   * debugger reports it as invalid rather than falling back. We published `en`
   * and `tr` from launch until 2026-09-22 — the tag was there, correct-looking,
   * and saying nothing.
   *
   * The territory is a *choice*, not a lookup, which is why it is written down
   * per locale rather than derived: `en` could be `en_US` or `en_GB`, and
   * Portuguese is on this roster for the Brazil exposure (see the note above),
   * so `pt` is `pt_BR` and would be wrong as `pt_PT`.
   */
  og: string
  /**
   * The language's name *in that language*. A switcher that offers a Turkish
   * speaker "Turkish" is asking them to read English to escape English.
   */
  label: string
  /** Writing direction, for `<html dir>`. No RTL locale yet; Arabic will want it. */
  dir: 'ltr' | 'rtl'
  /**
   * Launch tier, from the 2026-08-11 planning meeting.
   *
   *   1 — must be complete for the 1 September launch (English, Turkish)
   *   2 — next, if wanted (Spanish, German)
   *   3 — after that, if wanted (French, Portuguese)
   *
   * This is here so the coverage report can answer the question that actually
   * matters two weeks out — *"is the launch blocked?"* — rather than one average
   * across six languages, in which a complete English and an empty French come
   * out looking like a half-finished site.
   */
  tier: 1 | 2 | 3
  /**
   * Whether the PUBLIC site serves it today.
   *
   * A paused locale stays in `ALL_LOCALES` — it is not deleted, and that is the
   * point. Its content files, its coverage number and its place in the tier
   * order all survive, so re-opening it is this one boolean rather than a
   * re-import.
   *
   * Halil asked for the four incomplete ones to be paused on 2026-09-01, hours
   * after launch, and he was right: `es`, `de`, `fr` and `pt` were live at **2%
   * translation coverage** — four languages of almost entirely English pages,
   * in the sitemap, offered by the switcher, advertised to Google as
   * `hreflang` alternates. A language a visitor can select and then find
   * unwritten is worse than one we do not claim to speak.
   *
   * **Read `LOCALES` and not `ALL_LOCALES` unless you specifically want the
   * roster.** That split is deliberate and it fails in the safe direction: a
   * consumer that forgets this flag exists gets the live list, so the mistake
   * is "we did not publish something" rather than "we published something
   * unfinished". The one caller that genuinely wants everything is the
   * coverage report — it has to keep counting a paused locale, because that
   * number is how anyone learns it is ready to come back.
   */
  live: boolean
}

/**
 * Ordered as the switcher renders them, and the order is **launch priority, not
 * alphabetical** — decided in the 2026-08-11 meeting: English and Turkish for
 * 1 September, then Spanish and German, then French and Portuguese.
 *
 * Portuguese is on the list specifically for the Brazil exposure. Deliberately
 * excluded, and worth not re-proposing: Greek, Russian, Chinese, Japanese and
 * Arabic — *"alfabeler bozuluyor... hedef kitlemiz değil oralar."*
 */
export const ALL_LOCALES: LocaleDef[] = [
  { code: 'en', label: 'English', og: 'en_US', dir: 'ltr', tier: 1, live: true },
  { code: 'tr', label: 'Türkçe', og: 'tr_TR', dir: 'ltr', tier: 1, live: true },
  // Paused 2026-09-01 at 2% coverage each. `live: true` is all four need.
  { code: 'es', label: 'Español', og: 'es_ES', dir: 'ltr', tier: 2, live: false },
  { code: 'de', label: 'Deutsch', og: 'de_DE', dir: 'ltr', tier: 2, live: false },
  { code: 'fr', label: 'Français', og: 'fr_FR', dir: 'ltr', tier: 3, live: false },
  { code: 'pt', label: 'Português', og: 'pt_BR', dir: 'ltr', tier: 3, live: false },
]

/**
 * What the public site speaks. Routing, the switcher, `hreflang`, the sitemap
 * and the prerender pass all read this — so pausing a locale removes it from
 * every one of them at once, which is the only way that operation is safe to
 * do in a hurry.
 */
export const LOCALES: LocaleDef[] = ALL_LOCALES.filter((l) => l.live)

/** On the roster, not being served. The Worker redirects their URLs away. */
export const PAUSED_LOCALES: LocaleDef[] = ALL_LOCALES.filter((l) => !l.live)

export type LocaleCode = (typeof ALL_LOCALES)[number]['code']

/**
 * The base locale. Two jobs, and they are deliberately the same locale:
 * it is the content every other file is typed against, and the fallback
 * rendered for any string a translator has not reached yet.
 */
export const DEFAULT_LOCALE = 'en'

export const LOCALE_CODES: string[] = LOCALES.map((l) => l.code)

/** Every locale on the roster, paused ones included. Coverage reads this. */
export const ALL_LOCALE_CODES: string[] = ALL_LOCALES.map((l) => l.code)

export const PAUSED_LOCALE_CODES: string[] = PAUSED_LOCALES.map((l) => l.code)

/**
 * Whether this is a locale the site SERVES — `/es/...` is not routable while
 * Spanish is paused, and `App.tsx` renders its not-found for anything else.
 * The Worker sends those URLs to English before they ever reach the router, so
 * in practice this is the second line of defence rather than the first.
 */
export function isLocale(value: string | undefined): value is LocaleCode {
  return value !== undefined && LOCALE_CODES.includes(value)
}

/**
 * Reads the ROSTER, not the live list. A paused locale's label and direction
 * are still needed — the coverage report names it, and a build that referenced
 * `es` would otherwise silently get English's definition back.
 */
export function localeDef(code: string): LocaleDef {
  return ALL_LOCALES.find((l) => l.code === code) ?? ALL_LOCALES[0]
}

/**
 * Pick a locale from an `Accept-Language` header — used by the Worker to send
 * a bare `/` somewhere sensible.
 *
 * Quality-ordered, and matches on the primary subtag so `pt-BR` finds `pt` and
 * `de-AT` finds `de`. Falls back to the default rather than to the first thing
 * in the header: an unsupported language should get the base content, not
 * whatever happened to sort first.
 */
export function negotiateLocale(acceptLanguage: string | null): string {
  if (!acceptLanguage) return DEFAULT_LOCALE

  const ranked = acceptLanguage
    .split(',')
    .map((part) => {
      const [tag, ...params] = part.trim().split(';')
      const q = params.find((p) => p.trim().startsWith('q='))
      const quality = q ? Number.parseFloat(q.split('=')[1]) : 1
      return { tag: tag.trim().toLowerCase(), quality: Number.isFinite(quality) ? quality : 0 }
    })
    .filter((entry) => entry.tag.length > 0 && entry.quality > 0)
    .sort((a, b) => b.quality - a.quality)

  for (const { tag } of ranked) {
    const primary = tag.split('-')[0]
    const hit = LOCALE_CODES.find((code) => code === tag || code === primary)
    if (hit) return hit
  }
  return DEFAULT_LOCALE
}

/** Locales that must be complete before the site can go live. */
export const LAUNCH_LOCALES: string[] = ALL_LOCALES.filter((l) => l.tier === 1).map(
  (l) => l.code,
)
